import React, { useState } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { apiService } from '../services/apiService';

const VendorProfileModal = ({ 
  isOpen, 
  onClose, 
  vendor, 
  source = 'matching', // 'matching' or 'dashboard'
  themeId,
  entryPoint = 'direct',
  onContactVendor,
  onShowNotification
}) => {
  const [showContactModal, setShowContactModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState(3);
  const [isExistingVendor, setIsExistingVendor] = useState(false);
  const [shortlist, setShortlist] = useState([]);

  const showNotification = onShowNotification || ((type, title, message) => {
    // Fallback notification if parent doesn't provide one
    console.log(`${type}: ${title} - ${message}`);
  });

  // Track vendor view when modal opens
  React.useEffect(() => {
    if (isOpen && themeId && vendor?.id) {
      const trackView = async () => {
        try {
          await apiService.feedback.trackVendorInteraction({
            feature_type: 'vendor_matching',
            theme_id: themeId,
            vendor_id: vendor.id,
            interaction_type: 'view',
            compatibility_score: vendor.compatibility_score || null,
            entry_point: entryPoint
          });
        } catch (error) {
          console.error('Error tracking vendor view:', error);
        }
      };
      trackView();
    }
  }, [isOpen, themeId, vendor]);

  const getCategoryColor = (category) => {
    const colors = {
      'Venue': 'from-purple-500 to-indigo-600',
      'Photography': 'from-blue-500 to-cyan-600',
      'Catering': 'from-orange-500 to-red-600',
      'Flowers': 'from-pink-500 to-rose-600',
      'Music': 'from-green-500 to-teal-600',
      'Attire': 'from-purple-500 to-pink-600',
      'Transportation': 'from-gray-500 to-slate-600'
    };
    return colors[category] || 'from-rose-500 to-gold-500';
  };

  const addToShortlist = async (vendor) => {
    // Check authentication first
    const token = localStorage.getItem('token');
    if (!token) {
      showNotification('warning', 'Login Required', 'Please log in to save vendors to your shortlist');
      return;
    }

    // Track vendor interaction (shortlist)
    if (themeId && vendor.id) {
      try {
        await apiService.feedback.trackVendorInteraction({
          feature_type: 'vendor_matching',
          theme_id: themeId,
          vendor_id: vendor.id,
          interaction_type: 'shortlist',
          compatibility_score: vendor.compatibility_score || null,
          entry_point: entryPoint
        });
      } catch (error) {
        console.error('Error tracking vendor interaction:', error);
      }
    }

    // Reset existing vendor flag and show priority selection modal
    setIsExistingVendor(false);
    setShowPriorityModal(true);
  };

  const confirmAddToShortlist = async () => {
    const priority = selectedPriority;
    
    // For existing vendors, handle priority update logic
    if (isExistingVendor) {
      try {
        const token = localStorage.getItem('token');
        const checkResponse = await fetch(`${API_ENDPOINTS.vendors}/shortlist`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (checkResponse.ok) {
          const shortlistData = await checkResponse.json();
          const existingVendor = shortlistData.shortlists?.find(v => {
            let vendorName = v.vendor_name;
            if (v.vendor_data) {
              try {
                const vendorData = typeof v.vendor_data === 'string' 
                  ? JSON.parse(v.vendor_data) 
                  : v.vendor_data;
                vendorName = vendorName || vendorData.name;
              } catch (e) {
                console.log('Could not parse vendor_data for comparison');
              }
            }
            return vendorName === vendor.name;
          });
          
          const currentPriority = existingVendor?.priority || 3;
          
          if (currentPriority === selectedPriority) {
            // Same priority - just notify
            const priorityText = selectedPriority === 5 ? 'High' : selectedPriority === 3 ? 'Medium' : 'Low';
            showNotification('info', 'No Changes', `${vendor.name} is already in your shortlist with ${priorityText} priority.`);
            onClose();
            setShowPriorityModal(false);
            setIsExistingVendor(false);
            return;
          }
          
          // Different priority - update it
          const oldPriorityText = currentPriority === 5 ? 'High' : currentPriority === 3 ? 'Medium' : 'Low';
          const newPriorityText = selectedPriority === 5 ? 'High' : selectedPriority === 3 ? 'Medium' : 'Low';
          
          const updateResponse = await fetch(`${API_ENDPOINTS.vendors}/shortlist/update-priority`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              vendor_name: vendor.name,
              vendor_category: vendor.category.toLowerCase(),
              priority: selectedPriority
            })
          });

          if (updateResponse.ok) {
            showNotification('success', 'Priority Updated!', `${vendor.name}'s priority has been updated from ${oldPriorityText} to ${newPriorityText} priority.`);
            onClose();
            setShowPriorityModal(false);
            setIsExistingVendor(false);
          } else {
            showNotification('error', 'Update Failed', 'Failed to update vendor priority. Please try again.');
          }
        }
      } catch (error) {
        console.error('❌ Error updating priority:', error);
        showNotification('error', 'Error', 'Failed to update vendor priority. Please try again.');
      }
      return;
    }
    
    // New vendor - add to local shortlist first
    setShortlist(prev => [...prev, vendor]);
    
    // Save to Aurora database
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.vendors}/shortlist/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          vendor_name: vendor.name,
          vendor_category: vendor.category.toLowerCase(),
          vendor_data: {
            id: vendor.id,
            name: vendor.name,
            category: vendor.category,
            tagline: vendor.tagline,
            rating: vendor.rating,
            reviews: vendor.reviews,
            price: vendor.price,
            location: vendor.location,
            style: vendor.style,
            highlights: vendor.highlights,
            description: vendor.description || vendor.tagline
          },
          notes: vendor.tagline || '',
          priority: priority
        })
      });

      if (response.ok) {
        showNotification('success', 'Success!', `${vendor.name} has been added to your dashboard. You can view it in your Dashboard under "Your Shortlisted Vendors".`);
        onClose();
        setShowPriorityModal(false);
      } else {
        // Handle case where vendor already exists (shouldn't happen for new vendors, but just in case)
        if (response.status === 409 || response.status === 400) {
          // Set as existing vendor and show priority modal again
          setIsExistingVendor(true);
          // Modal is already open, just update the state
        } else {
          let errorMessage = 'Unknown error';
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || 'Unknown error';
          } catch (e) {
            errorMessage = response.statusText || 'Unknown error';
          }
          showNotification('error', 'Save Failed', `Failed to save vendor: ${errorMessage}`);
        }
      }
    } catch (error) {
      console.error('Error saving vendor:', error);
      showNotification('error', 'Save Failed', 'There was an error saving the vendor. Please try again.');
    }
  };

  const openContactModal = async (vendor) => {
    // Track vendor interaction (contact)
    if (themeId && vendor.id) {
      try {
        await apiService.feedback.trackVendorInteraction({
          feature_type: 'vendor_matching',
          theme_id: themeId,
          vendor_id: vendor.id,
          interaction_type: 'contact',
          compatibility_score: vendor.compatibility_score || null,
          entry_point: entryPoint
        });
      } catch (error) {
        console.error('Error tracking vendor interaction:', error);
      }
    }

    setShowContactModal(true);
    if (onContactVendor) {
      onContactVendor(vendor);
    }
  };

  if (!isOpen || !vendor) return null;

  return (
    <>
      {/* Main Vendor Profile Modal */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-lg z-50 flex items-center justify-center p-4 animate-in fade-in duration-500">
        <div className="bg-gradient-to-br from-white/95 via-rose-50/30 to-champagne-50/30 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-white/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto transform animate-in zoom-in slide-in-from-bottom-4 duration-700">
          
          {/* Floating VowAI Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl">
            {/* Magical sparkles */}
            <div className="absolute top-4 left-8 text-white/30 text-xl animate-pulse">✨</div>
            <div className="absolute top-8 right-16 text-white/40 text-lg animate-pulse" style={{animationDelay: '0.5s'}}>💫</div>
            <div className="absolute bottom-6 left-16 text-white/30 text-sm animate-pulse" style={{animationDelay: '1s'}}>⭐</div>
            
            {/* Floating hearts */}
            <div className="absolute top-12 right-8 text-rose-300/20 text-2xl animate-bounce" style={{animationDelay: '0.3s', animationDuration: '3s'}}>💖</div>
            <div className="absolute bottom-12 left-8 text-pink-300/25 text-xl animate-bounce" style={{animationDelay: '1.5s', animationDuration: '4s'}}>💕</div>
            
            {/* Magical orbs */}
            <div className="absolute top-16 right-20 w-16 h-16 bg-gradient-to-br from-rose-200/10 to-pink-300/15 rounded-full blur-xl animate-pulse" style={{animationDelay: '0s', animationDuration: '4s'}}></div>
            <div className="absolute bottom-20 left-12 w-20 h-20 bg-gradient-to-br from-champagne-200/10 to-gold-300/15 rounded-full blur-2xl animate-pulse" style={{animationDelay: '2s', animationDuration: '5s'}}></div>
          </div>

          {/* Modal Header */}
          <div className={`bg-gradient-to-r ${getCategoryColor(vendor.category)} p-8 rounded-t-3xl relative overflow-hidden`}>
            {/* Header magical elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/5 rounded-full blur-3xl"></div>
              <div className="absolute top-4 left-8 text-white/20 text-2xl animate-pulse">✨</div>
              <div className="absolute top-8 right-16 text-white/30 text-lg animate-pulse" style={{animationDelay: '0.5s'}}>💫</div>
              <div className="absolute bottom-6 left-16 text-white/20 text-sm animate-pulse" style={{animationDelay: '1s'}}>⭐</div>
            </div>
            
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 hover:rotate-90 transition-all duration-300 z-10"
            >
              <span className="text-xl">✕</span>
            </button>
            
            <div className="flex items-center space-x-6 relative z-10">
              <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center shadow-lg backdrop-blur-sm border border-white/30">
                <span className="text-5xl">{vendor.image || '🏢'}</span>
              </div>
              <div>
                <h2 className="text-3xl font-serif font-bold text-white mb-2 drop-shadow-lg">
                  {vendor.name}
                </h2>
                <p className="text-white/90 italic text-lg drop-shadow-md">
                  {vendor.tagline || 'Professional wedding service provider'}
                </p>
                <div className="flex items-center space-x-4 mt-3">
                  <div className="flex items-center space-x-1">
                    <span className="text-yellow-300">⭐</span>
                    <span className="font-bold text-white">{vendor.rating || '4.5'}</span>
                    <span className="text-white/80">({vendor.reviews || '50'} reviews)</span>
                  </div>
                  <span className="px-3 py-1 bg-white/60 rounded-full text-sm font-bold text-gray-700">
                    {vendor.category}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Content */}
          <div className="p-8 space-y-6 relative">
            
            {/* Floating content elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-10 right-8 text-rose-200/20 text-xl animate-bounce" style={{animationDelay: '0.8s', animationDuration: '4s'}}>💍</div>
              <div className="absolute bottom-20 left-6 text-champagne-300/25 text-lg animate-bounce" style={{animationDelay: '2s', animationDuration: '3.5s'}}>🌸</div>
            </div>
            
            {/* Description */}
            <div className="relative">
              <div className="absolute -top-2 -left-2 w-full h-full bg-gradient-to-br from-rose-100/20 to-pink-100/20 rounded-xl blur-sm"></div>
              <div className="relative bg-gradient-to-br from-white/80 to-rose-50/40 rounded-xl p-6 border border-white/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                  <span className="text-2xl mr-2 animate-bounce">✨</span>
                  About
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {vendor.description || 'Professional wedding service provider committed to making your special day perfect.'}
                </p>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="group bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl p-4 border border-rose-200 hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl group-hover:animate-bounce">📍</span>
                    <span className="font-bold text-gray-700">Location</span>
                  </div>
                  <div className="text-gray-800 font-medium">{vendor.location || 'Various'}</div>
                </div>
                
                <div className="group bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200 hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl group-hover:animate-bounce">💰</span>
                    <span className="font-bold text-gray-700">Price Range</span>
                  </div>
                  <div className="text-gray-800 font-medium">{vendor.price_range || vendor.price || 'Contact for pricing'}</div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="group bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200 hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl group-hover:animate-bounce">🎨</span>
                    <span className="font-bold text-gray-700">Style</span>
                  </div>
                  <div className="text-gray-800 font-medium">{vendor.style || 'Classic'}</div>
                </div>
                
                <div className="group bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-200 hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl group-hover:animate-bounce">⭐</span>
                    <span className="font-bold text-gray-700">Rating</span>
                  </div>
                  <div className="text-gray-800 font-medium">{vendor.rating || '4.5'}/5.0</div>
                </div>
              </div>
            </div>

            {/* Services */}
            <div className="relative">
              <div className="absolute -top-2 -left-2 w-full h-full bg-gradient-to-br from-champagne-100/20 to-gold-100/20 rounded-xl blur-sm"></div>
              <div className="relative bg-gradient-to-br from-white/80 to-champagne-50/40 rounded-xl p-6 border border-white/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                  <span className="text-2xl mr-2 animate-bounce">🌟</span>
                  Services
                </h3>
                <div className="flex flex-wrap gap-2">
                  {(vendor.services || vendor.specialties || ['Full Service', 'Consultation', 'Custom Packages']).map((service, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gradient-to-r from-gold-100 to-yellow-100 text-gold-700 rounded-full text-sm font-medium border border-gold-200 hover:shadow-md transition-all duration-300 transform hover:scale-105"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Portfolio */}
            <div className="relative">
              <div className="absolute -top-2 -left-2 w-full h-full bg-gradient-to-br from-lavender-100/20 to-purple-100/20 rounded-xl blur-sm"></div>
              <div className="relative bg-gradient-to-br from-white/80 to-lavender-50/40 rounded-xl p-6 border border-white/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                  <span className="text-2xl mr-2 animate-bounce">💎</span>
                  Portfolio Highlights
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {(vendor.portfolio || ['Sample Work 1', 'Sample Work 2', 'Sample Work 3']).map((item, index) => (
                    <div
                      key={index}
                      className="group bg-gradient-to-r from-white/60 to-rose-50/60 rounded-xl p-4 border border-white/50 text-center hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                    >
                      <div className="text-2xl mb-2 group-hover:animate-bounce">📸</div>
                      <div className="text-sm font-medium text-gray-700">{item}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={`flex gap-4 pt-4 ${source === 'dashboard' ? 'justify-center' : ''} relative`}>
              {/* Magical button glow effects */}
              <div className="absolute inset-0 bg-gradient-to-r from-rose-300/10 via-pink-300/10 to-purple-300/10 rounded-2xl blur-xl animate-pulse"></div>
              
              <button 
                onClick={onClose}
                className="group relative overflow-hidden bg-gradient-to-r from-gray-400 to-gray-500 text-white py-3 px-6 rounded-xl font-bold hover:from-gray-500 hover:to-gray-600 hover:shadow-xl transform hover:scale-105 transition-all duration-300 border-2 border-white/20"
              >
                {/* Button shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <div className="relative flex items-center justify-center">
                  <span className="mr-2 text-xl group-hover:animate-bounce">👈</span>
                  Back
                </div>
              </button>
              
              <button 
                onClick={() => openContactModal(vendor)}
                className={`${source === 'dashboard' ? 'w-64' : 'flex-1'} group relative overflow-hidden bg-gradient-to-r from-rose-500 to-gold-500 text-white py-3 px-6 rounded-xl font-bold hover:from-rose-600 hover:to-gold-600 hover:shadow-xl transform hover:scale-105 transition-all duration-300 border-2 border-white/20`}
              >
                {/* Button shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <div className="relative flex items-center justify-center">
                  <span className="mr-2 text-xl group-hover:animate-bounce">📞</span>
                  Contact Vendor
                </div>
              </button>
              {source === 'matching' && (
                <button 
                  onClick={() => addToShortlist(vendor)}
                  className="flex-1 group relative overflow-hidden bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 px-6 rounded-xl font-bold hover:from-purple-600 hover:to-pink-600 hover:shadow-xl transform hover:scale-105 transition-all duration-300 border-2 border-white/20"
                >
                  {/* Button shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  <div className="relative flex items-center justify-center">
                    <span className="mr-2 text-xl group-hover:animate-bounce">💖</span>
                    Add to Shortlist
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-rose-200/50 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
            
            {/* Floating Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-rose-200/30 to-gold-200/30 rounded-full blur-xl"></div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-gradient-to-tr from-purple-200/30 to-pink-200/30 rounded-full blur-xl"></div>
            </div>

            {/* Modal Header */}
            <div className="relative bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 p-8 rounded-t-3xl">
              <button
                onClick={() => setShowContactModal(false)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
              >
                <span className="text-xl">✕</span>
              </button>
              
              {/* Floating sparkles */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-4 left-8 text-white/30 text-xl animate-pulse">✨</div>
                <div className="absolute top-8 right-16 text-white/40 text-lg animate-pulse" style={{animationDelay: '0.5s'}}>💫</div>
                <div className="absolute bottom-6 left-16 text-white/30 text-sm animate-pulse" style={{animationDelay: '1s'}}>⭐</div>
              </div>

              <div className="text-center relative z-10">
                <div className="text-5xl mb-4 animate-bounce">📞</div>
                <h2 className="text-3xl font-serif font-bold text-white mb-2">Contact {vendor.name}</h2>
                <p className="text-white/90 text-lg font-medium">{vendor.category}</p>
                <div className="flex items-center justify-center mt-3 space-x-2">
                  <div className="flex text-yellow-300">
                    {'★'.repeat(Math.floor(vendor.rating || 4.5))}
                  </div>
                  <span className="text-white/90 font-medium">{vendor.rating || '4.5'}</span>
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <div className="relative p-8 space-y-6">
              
              {/* Phone */}
              <div className="group hover:scale-105 transition-all duration-300">
                <div className="flex items-center space-x-6 p-6 bg-gradient-to-r from-rose-50 via-pink-50 to-gold-50 rounded-2xl border-2 border-rose-200/50 hover:border-rose-300/70 hover:shadow-xl transition-all duration-300">
                  <div className="text-4xl group-hover:animate-bounce">📱</div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-800 text-lg mb-1">Phone Number</div>
                    <div className="text-gray-600 text-lg font-medium">+1 (555) 123-4567</div>
                    <div className="text-sm text-gray-500 mt-1">Available 9 AM - 6 PM</div>
                  </div>
                  <div className="text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="group hover:scale-105 transition-all duration-300">
                <div className="flex items-center space-x-6 p-6 bg-gradient-to-r from-purple-50 via-pink-50 to-rose-50 rounded-2xl border-2 border-purple-200/50 hover:border-purple-300/70 hover:shadow-xl transition-all duration-300">
                  <div className="text-4xl group-hover:animate-bounce">📧</div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-800 text-lg mb-1">Email Address</div>
                    <div className="text-gray-600 text-lg font-medium">{vendor.name.toLowerCase().replace(/\s+/g, '')}@email.com</div>
                    <div className="text-sm text-gray-500 mt-1">Response within 24 hours</div>
                  </div>
                  <div className="text-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="group hover:scale-105 transition-all duration-300">
                <div className="flex items-center space-x-6 p-6 bg-gradient-to-r from-gold-50 via-yellow-50 to-orange-50 rounded-2xl border-2 border-gold-200/50 hover:border-gold-300/70 hover:shadow-xl transition-all duration-300">
                  <div className="text-4xl group-hover:animate-bounce">📍</div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-800 text-lg mb-1">Business Address</div>
                    <div className="text-gray-600 text-lg font-medium">123 Wedding Street</div>
                    <div className="text-gray-600 text-lg font-medium">{vendor.location}</div>
                    <div className="text-sm text-gray-500 mt-1">By appointment only</div>
                  </div>
                  <div className="text-gold-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Website */}
              <div className="group hover:scale-105 transition-all duration-300">
                <div className="flex items-center space-x-6 p-6 bg-gradient-to-r from-teal-50 via-cyan-50 to-blue-50 rounded-2xl border-2 border-teal-200/50 hover:border-teal-300/70 hover:shadow-xl transition-all duration-300">
                  <div className="text-4xl group-hover:animate-bounce">🌐</div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-800 text-lg mb-1">Website</div>
                    <div className="text-gray-600 text-lg font-medium">www.{vendor.name.toLowerCase().replace(/\s+/g, '')}.com</div>
                    <div className="text-sm text-gray-500 mt-1">Portfolio & booking available</div>
                  </div>
                  <div className="text-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.559-.499-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.559.499.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.497-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd"/>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center pt-6">
                <button 
                  onClick={() => setShowContactModal(false)}
                  className="w-full bg-gradient-to-r from-gray-200 to-gray-300 text-gray-700 py-4 px-6 rounded-2xl font-bold hover:from-gray-300 hover:to-gray-400 hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                >
                  <span className="mr-2">👈</span>
                  Back
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Priority Selection Modal */}
      {showPriorityModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-rose-200/50 max-w-lg w-full">
            
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 p-6 rounded-t-3xl relative">
              <button
                onClick={() => setShowPriorityModal(false)}
                className="absolute top-4 right-4 text-white/80 hover:text-white text-2xl font-bold hover:rotate-90 transition-all duration-300"
              >
                ×
              </button>
              
              <div className="text-center">
                <div className="text-3xl mb-2 animate-bounce">⭐</div>
                <h2 className="text-xl font-bold text-white mb-1">Set Priority for {vendor.name}</h2>
                <p className="text-white/90 text-sm">Choose how important this vendor is to you</p>
              </div>
            </div>

            {/* Priority Options */}
            <div className="p-6 space-y-4">
              
              {/* High Priority */}
              <div 
                onClick={() => setSelectedPriority(5)}
                className={`cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                  selectedPriority === 5 
                    ? 'border-red-300 bg-gradient-to-r from-red-50 to-pink-50 shadow-lg' 
                    : 'border-gray-200 bg-gray-50 hover:border-red-200 hover:bg-red-50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="text-2xl">🔥</div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-800">High Priority</div>
                    <div className="text-sm text-gray-600">Must-have vendor for your wedding</div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedPriority === 5 ? 'bg-red-500 border-red-500' : 'border-gray-300'
                  }`}></div>
                </div>
              </div>

              {/* Medium Priority */}
              <div 
                onClick={() => setSelectedPriority(3)}
                className={`cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                  selectedPriority === 3 
                    ? 'border-yellow-300 bg-gradient-to-r from-yellow-50 to-orange-50 shadow-lg' 
                    : 'border-gray-200 bg-gray-50 hover:border-yellow-200 hover:bg-yellow-50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="text-2xl">⚡</div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-800">Medium Priority</div>
                    <div className="text-sm text-gray-600">Important but flexible option</div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedPriority === 3 ? 'bg-yellow-500 border-yellow-500' : 'border-gray-300'
                  }`}></div>
                </div>
              </div>

              {/* Low Priority */}
              <div 
                onClick={() => setSelectedPriority(1)}
                className={`cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                  selectedPriority === 1 
                    ? 'border-green-300 bg-gradient-to-r from-green-50 to-teal-50 shadow-lg' 
                    : 'border-gray-200 bg-gray-50 hover:border-green-200 hover:bg-green-50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="text-2xl">🌱</div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-800">Low Priority</div>
                    <div className="text-sm text-gray-600">Nice to have, budget permitting</div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedPriority === 1 ? 'bg-green-500 border-green-500' : 'border-gray-300'
                  }`}></div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button 
                  onClick={() => setShowPriorityModal(false)}
                  className="flex-1 bg-gradient-to-r from-gray-200 to-gray-300 text-gray-700 py-3 px-4 rounded-xl font-bold hover:from-gray-300 hover:to-gray-400 hover:shadow-lg transition-all duration-300"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmAddToShortlist}
                  className="flex-1 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 text-white py-3 px-4 rounded-xl font-bold hover:from-purple-600 hover:via-pink-600 hover:to-rose-600 hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  <span className="mr-2">💖</span>
                  Add to Shortlist
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VendorProfileModal;
